import React, { useRef, useEffect, useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Link,
  Image,
  Table as TableIcon,
  Quote,
  Code,
  Minus,
  Undo,
  Redo,
  Eraser,
  Palette,
} from "lucide-react";

interface RichTextEditorProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}

const COLORS = [
  { name: "Default", value: "var(--text)" },
  { name: "Orange/Red", value: "#BF3D0C" },
  { name: "Blue", value: "#2563eb" },
  { name: "Green", value: "#16a34a" },
  { name: "Purple", value: "#9333ea" },
  { name: "Muted Gray", value: "var(--text3)" },
];

export default function RichTextEditor({ value, onChange, placeholder }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isFirstMount = useRef(true);
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Sync state to editor
  useEffect(() => {
    if (editorRef.current) {
      if (isFirstMount.current || (value !== editorRef.current.innerHTML && document.activeElement !== editorRef.current)) {
        editorRef.current.innerHTML = value || "<p><br></p>";
        isFirstMount.current = false;
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execute = (command: string, arg: string = "") => {
    document.execCommand(command, false, arg);
    handleInput();
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const handleLink = () => {
    const url = prompt("Enter the link URL (e.g. https://example.com):");
    if (url) {
      execute("createLink", url);
    }
  };

  const handleImage = () => {
    const url = prompt("Enter the image URL:");
    if (url) {
      const html = `<img src="${url}" alt="Blog Image" style="max-width: 100%; border-radius: 8px; margin: 16px auto; display: block;" />`;
      execute("insertHTML", html);
    }
  };

  const handleTable = () => {
    const rows = prompt("Enter number of rows:", "3");
    const cols = prompt("Enter number of columns:", "3");
    if (rows && cols) {
      const rCount = parseInt(rows, 10);
      const cCount = parseInt(cols, 10);
      if (isNaN(rCount) || isNaN(cCount)) return;

      let tableHtml = `<table style="width: 100%; border-collapse: collapse; margin: 16px 0; border: 1px solid var(--border);">`;
      
      // Header row
      tableHtml += `<thead><tr style="background: rgba(255, 255, 255, 0.03);">`;
      for (let j = 0; j < cCount; j++) {
        tableHtml += `<th style="border: 1px solid var(--border); padding: 8px 12px; font-weight: 600; text-align: left;">Header ${j + 1}</th>`;
      }
      tableHtml += `</tr></thead><tbody>`;

      // Body rows
      for (let i = 0; i < rCount; i++) {
        tableHtml += `<tr>`;
        for (let j = 0; j < cCount; j++) {
          tableHtml += `<td style="border: 1px solid var(--border); padding: 8px 12px;">Cell data</td>`;
        }
        tableHtml += `</tr>`;
      }
      tableHtml += `</tbody></table>`;
      execute("insertHTML", tableHtml);
    }
  };

  const handleBlockquote = () => {
    execute("formatBlock", "<blockquote>");
  };

  const handleCodeBlock = () => {
    execute("formatBlock", "<pre>");
  };

  const handleDivider = () => {
    const html = `<hr style="border: none; border-top: 1px solid var(--border); margin: 28px 0;" />`;
    execute("insertHTML", html);
  };

  const handleHeading = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val) {
      execute("formatBlock", `<${val}>`);
      e.target.value = ""; // Reset select index
    }
  };

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "var(--card-solid)" }}>
      {/* Styles for rendering elements inside the editor */}
      <style>{`
        .rich-editor-canvas {
          min-height: 280px;
          padding: 16px 20px;
          color: var(--text);
          outline: none;
          font-size: 14px;
          line-height: 1.75;
          font-family: 'DM Sans', sans-serif;
        }
        .rich-editor-canvas p {
          margin: 0 0 14px;
        }
        .rich-editor-canvas h1, 
        .rich-editor-canvas h2, 
        .rich-editor-canvas h3, 
        .rich-editor-canvas h4 {
          margin: 28px 0 12px;
          font-weight: 600;
          color: var(--text);
          line-height: 1.3;
        }
        .rich-editor-canvas h1 { font-size: 22px; }
        .rich-editor-canvas h2 { font-size: 18px; }
        .rich-editor-canvas h3 { font-size: 16px; }
        .rich-editor-canvas h4 { font-size: 14px; }
        
        .rich-editor-canvas blockquote {
          border-left: 3px solid var(--or);
          background: rgba(191, 61, 12, 0.06);
          padding: 12px 18px;
          margin: 16px 0;
          border-radius: 0 8px 8px 0;
          font-style: italic;
          color: var(--text2);
        }
        .rich-editor-canvas pre {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid var(--border);
          padding: 12px 16px;
          border-radius: 8px;
          font-family: 'Fira Code', monospace;
          font-size: 13px;
          overflow-x: auto;
          margin: 16px 0;
          color: #e08b8b;
        }
        .rich-editor-canvas ul, 
        .rich-editor-canvas ol {
          margin: 0 0 14px;
          padding-left: 24px;
        }
        .rich-editor-canvas li {
          margin-bottom: 6px;
        }
        .rich-editor-canvas table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }
        .rich-editor-canvas table th, 
        .rich-editor-canvas table td {
          border: 1px solid var(--border);
          padding: 8px 12px;
          text-align: left;
        }
        .rich-editor-canvas table th {
          background: rgba(255, 255, 255, 0.03);
          font-weight: 600;
        }
        .rich-editor-canvas img {
          max-width: 100%;
          border-radius: 8px;
          margin: 20px auto;
          display: block;
        }
        .rte-tool-btn {
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justifyContent: center;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 6px;
          color: var(--text3);
          cursor: pointer;
          transition: all 0.15s;
        }
        .rte-tool-btn:hover {
          color: var(--text);
          background: rgba(255, 255, 255, 0.06);
          border-color: var(--border);
        }
        .rte-tool-btn.active {
          color: var(--or);
          background: var(--or-dim);
          border-color: rgba(191, 61, 12, 0.35);
        }
        .rte-divider {
          width: 1px;
          height: 20px;
          background: var(--border);
          margin: 0 4px;
        }
      `}</style>

      {/* Toolbar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "8px 12px",
        background: "rgba(255, 255, 255, 0.02)",
        borderBottom: "1px solid var(--border)",
        flexWrap: "wrap",
        position: "sticky",
        top: 0,
        zIndex: 10
      }}>
        {/* Undo/Redo */}
        <button type="button" className="rte-tool-btn" onClick={() => execute("undo")} title="Undo"><Undo size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={() => execute("redo")} title="Redo"><Redo size={14} /></button>
        
        <div className="rte-divider" />

        {/* Headings */}
        <select 
          onChange={handleHeading} 
          style={{
            background: "var(--card-solid)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: "3px 8px",
            fontSize: 12,
            color: "var(--text2)",
            outline: "none",
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif"
          }}
          defaultValue=""
        >
          <option value="" disabled>Format Text...</option>
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
        </select>

        <div className="rte-divider" />

        {/* Styling */}
        <button type="button" className="rte-tool-btn" onClick={() => execute("bold")} title="Bold"><Bold size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={() => execute("italic")} title="Italic"><Italic size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={() => execute("underline")} title="Underline"><Underline size={14} /></button>
        
        <div className="rte-divider" />

        {/* Alignments */}
        <button type="button" className="rte-tool-btn" onClick={() => execute("justifyLeft")} title="Align Left"><AlignLeft size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={() => execute("justifyCenter")} title="Align Center"><AlignCenter size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={() => execute("justifyRight")} title="Align Right"><AlignRight size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={() => execute("justifyFull")} title="Justify"><AlignJustify size={14} /></button>

        <div className="rte-divider" />

        {/* Lists */}
        <button type="button" className="rte-tool-btn" onClick={() => execute("insertUnorderedList")} title="Bullet List"><List size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={() => execute("insertOrderedList")} title="Numbered List"><ListOrdered size={14} /></button>

        <div className="rte-divider" />

        {/* Colors */}
        <div style={{ position: "relative" }}>
          <button 
            type="button" 
            className={`rte-tool-btn ${showColorPicker ? "active" : ""}`} 
            onClick={() => setShowColorPicker(!showColorPicker)} 
            title="Text Color"
          >
            <Palette size={14} />
          </button>
          {showColorPicker && (
            <div style={{
              position: "absolute",
              top: "100%",
              left: 0,
              marginTop: 6,
              zIndex: 20,
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: 8,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
              minWidth: 120
            }}>
              {COLORS.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => {
                    execute("foreColor", c.value);
                    setShowColorPicker(false);
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--text)",
                    fontSize: 12,
                    textAlign: "left",
                    padding: "6px 8px",
                    borderRadius: 4,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.value, border: "1px solid rgba(255,255,255,0.15)" }} />
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rte-divider" />

        {/* Inserts */}
        <button type="button" className="rte-tool-btn" onClick={handleLink} title="Insert Link"><Link size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={handleImage} title="Insert Image Link"><Image size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={handleTable} title="Insert Table"><TableIcon size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={handleBlockquote} title="Blockquote"><Quote size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={handleCodeBlock} title="Code Block"><Code size={14} /></button>
        <button type="button" className="rte-tool-btn" onClick={handleDivider} title="Horizontal Divider"><Minus size={14} /></button>

        <div className="rte-divider" />

        {/* Clear formatting */}
        <button type="button" className="rte-tool-btn" onClick={() => execute("removeFormat")} title="Clear Formatting"><Eraser size={14} /></button>
      </div>

      {/* Editing Canvas */}
      <div
        ref={editorRef}
        className="rich-editor-canvas"
        contentEditable
        onInput={handleInput}
        style={{ color: "var(--text)" }}
      />
    </div>
  );
}
